import i18n from 'helpers/i18n';
import messages from './messages';
import React, { forwardRef, useEffect, useState } from 'react';
import { createUserAnswers, updateUserAnswers } from 'actions/userAnswers';
import {
  createUserAnswers as createUserPreparationAnswers,
  updateUserAnswers as updateUserPreparationAnswers
} from 'actions/testPreparations';
import { toastError } from 'components/Toast';

const SCORES = [1, 2, 3, 4, 5, 6, 7];

type UserAnswer = { id: number; answer: number };
type DimensionScore = { id: number; answer: number; dimensionId: number };
interface DimensionScoreInputProps {
  /** ID for the current Reliability Test */
  testAttemptId: string;

  /** Dimension Id */
  dimensionId: number;

  /** Current Coding Video  */
  codingVideoId: number;

  /** User Answer object. Contains ID and value. */
  userAnswer: UserAnswer | null;

  /** Updates the user scores. Useful to enable the submit button once completed */
  addUserScore: (value: DimensionScore) => void;

  /** Updates user scores based on test type */
  testType?: 'reliability' | 'testPreparations';

  /** Dimension label for screen reader context */
  dimensionLabel: string;

  /** Whether this dimension is missing a valid code after a failed submission */
  hasError?: boolean;

  /** Field-specific error message shown and announced when hasError is true */
  errorMessage?: string;
}

const DimensionScoreInput = forwardRef<
  HTMLFieldSetElement,
  DimensionScoreInputProps
>(function DimensionScoreInput(
  {
    testAttemptId,
    dimensionId,
    codingVideoId,
    userAnswer,
    addUserScore,
    testType,
    dimensionLabel,
    hasError = false,
    errorMessage
  },
  ref
) {
  const [currentUserAnswer, setCurrentUserAnswer] = useState<UserAnswer | null>(
    userAnswer
  );
  const radioGroupName = `dimension-score-${testAttemptId}-${dimensionId}`;
  const radioLegendId = `${radioGroupName}-legend`;
  const errorId = `${radioGroupName}-error`;

  useEffect(() => {
    setCurrentUserAnswer(userAnswer);
  }, [userAnswer]);

  function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
    const dimensionScore = parseInt(event.target.value, 10);
    let action = currentUserAnswer ? updateUserAnswers : createUserAnswers;

    if (testType === 'testPreparations') {
      action = currentUserAnswer
        ? updateUserPreparationAnswers
        : createUserPreparationAnswers;
    }

    if (dimensionScore !== currentUserAnswer?.answer) {
      action(testAttemptId, {
        coding_video_id: codingVideoId,
        dimension_id: dimensionId,
        answer: dimensionScore,
        id: currentUserAnswer?.id
      })
        .then(response => {
          setCurrentUserAnswer({
            id: response.data.id,
            answer: dimensionScore
          });
          addUserScore({
            id: response.data.id,
            answer: dimensionScore,
            dimensionId: dimensionId
          });
        })
        .catch(() => {
          toastError({ description: i18n.ft(messages.error) });
        });
    }
  }

  return (
    <fieldset
      ref={ref}
      tabIndex={-1}
      aria-labelledby={radioLegendId}
      aria-describedby={hasError ? errorId : undefined}
      aria-invalid={hasError || undefined}
      className="grid w-auto grid-cols-7 items-center justify-items-center gap-[7px] sm:gap-8"
    >
      <legend id={radioLegendId} className="sr-only">
        {dimensionLabel}
      </legend>
      {SCORES.map(score => (
        <label
          key={score}
          className="relative inline-flex cursor-pointer items-center justify-center"
        >
          <input
            type="radio"
            name={radioGroupName}
            value={score}
            checked={score === currentUserAnswer?.answer}
            onChange={handleChange}
            className="peer sr-only"
            aria-label={i18n.ft(messages.dimensionScore, { value: score })}
          />
          <span className="inline-flex h-[34px] w-[34px] items-center justify-center rounded-full border-[3px] border-transparent bg-white text-base font-bold leading-[14px] text-[#364A5E] peer-checked:bg-[#4A8263] peer-checked:text-white peer-focus-visible:outline-none peer-focus-visible:ring-2 peer-focus-visible:ring-[#00779E] peer-focus-visible:ring-offset-2 sm:h-8 sm:w-8 sm:leading-5 sm:shadow-[0.1px_1px_4px_rgba(0,0,0,0.25)]">
            {score}
          </span>
        </label>
      ))}
      {hasError && errorMessage && (
        <span
          id={errorId}
          className="col-span-7 mt-1 block text-left text-sm font-normal text-ds-text-negative"
        >
          {errorMessage}
        </span>
      )}
    </fieldset>
  );
});

export default DimensionScoreInput;
